import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createContext, useContext, useMemo } from 'react';
/**
 * AnalyticsTrackingProvider lets you inject or update analytics context (e.g., consultation)
 * for use with `useEmitUiEvent` and other Snowplow-based analytics logic.
 *
 * You pass an `update` function, which receives the current tracking context and returns a new one.
 * This enables layered tracking — for example, setting a `section_id` for a specific screen.
 *
 * Feature Flags:
 * - Optionally pass a `flags` object (already shaped for analytics) to set under the `flags` branch.
 *   Example shape: `{ flags: [{ name: 'feature_release_1', value: 'variant', vendor: 'launchdarkly' }] }`
 * - If you don’t pass flags, this provider will keep any existing flags from a parent context.
 * - If you do pass flags, it will replace the parent’s flags entirely — it does not merge them.
 *
 * ⚠️ Note:
 * - This provider does not enforce merging or protect against accidental overwrites.
 *   If you want to preserve existing non-flag values, explicitly merge them inside your `update` function.
 * - To avoid unnecessary recalculations on re-render, wrap your `update` function in `useCallback`
 *   so the context value is only recomputed when necessary.
 *
 * Example:
 * ```tsx
 * const updateTrackingContext = useCallback(
 *   (ctx) => ({
 *     ...ctx,
 *     consultation: { ...ctx.consultation, consultation_id: '123' },
 *   }),
 *   []
 * );
 *
 * // With flags (note: flags is an ARRAY of items under a `flags` key)
 * <AnalyticsTrackingProvider
 *   flags={{ flags: [{ name: 'my_feature', value: 'variant', vendor: 'launchdarkly' }] }}
 *   update={updateTrackingContext}
 * >
 *   <SomeComponent />
 * </AnalyticsTrackingProvider>
 *
 * // Without flags (inherits from parent if present)
 * <AnalyticsTrackingProvider update={updateTrackingContext}>
 *   <SomeComponent />
 * </AnalyticsTrackingProvider>
 * ```
 */
const AnalyticsTrackingContext = createContext(null);
export const AnalyticsTrackingProvider = ({ children, flags, update }) => {
    const context = useContext(AnalyticsTrackingContext);
    const value = useMemo(() => {
        const currentContext = context ?? {};
        const updated = update ? update(currentContext) : currentContext;
        return flags ? { ...updated, flags } : updated;
    }, [context, update, flags]);
    return _jsx(AnalyticsTrackingContext.Provider, { value: value, children: children });
};
/**
 * Returns the current analytics context from AnalyticsTrackingProvider.
 * If no provider is present, returns an empty object instead of throwing.
 */
export const useAnalyticsTracking = () => useContext(AnalyticsTrackingContext) ?? {};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiQW5hbHl0aWNzVHJhY2tpbmdDb250ZXh0LmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vc3JjL2xpYi9jb250ZXh0L0FuYWx5dGljc1RyYWNraW5nQ29udGV4dC50c3giXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IjtBQUNBLE9BQU8sRUFBRSxhQUFhLEVBQUUsVUFBVSxFQUFFLE9BQU8sRUFBRSxNQUFNLE9BQU8sQ0FBQztBQUczRDs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBMENHO0FBRUgsTUFBTSx3QkFBd0IsR0FBRyxhQUFhLENBQWdDLElBQUksQ0FBQyxDQUFDO0FBUXBGLE1BQU0sQ0FBQyxNQUFNLHlCQUF5QixHQUFHLENBQUMsRUFBRSxRQUFRLEVBQUUsS0FBSyxFQUFFLE1BQU0sRUFBa0MsRUFBRSxFQUFFO0lBQ3ZHLE1BQU0sT0FBTyxHQUFHLFVBQVUsQ0FBQyx3QkFBd0IsQ0FBQyxDQUFDO0lBRXJELE1BQU0sS0FBSyxHQUFHLE9BQU8sQ0FBeUIsR0FBRyxFQUFFO1FBQ2pELE1BQU0sY0FBYyxHQUFHLE9BQU8sSUFBSSxFQUFFLENBQUM7UUFDckMsTUFBTSxPQUFPLEdBQUcsTUFBTSxDQUFDLENBQUMsQ0FBQyxNQUFNLENBQUMsY0FBYyxDQUFDLENBQUMsQ0FBQyxDQUFDLGNBQWMsQ0FBQztRQUVqRSxPQUFPLEtBQUssQ0FBQyxDQUFDLENBQUMsRUFBRSxHQUFHLE9BQU8sRUFBRSxLQUFLLEVBQUUsQ0FBQyxDQUFDLENBQUMsT0FBTyxDQUFDO0lBQ2pELENBQUMsRUFBRSxDQUFDLE9BQU8sRUFBRSxNQUFNLEVBQUUsS0FBSyxDQUFDLENBQUMsQ0FBQztJQUU3QixPQUFPLEtBQUMsd0JBQXdCLENBQUMsUUFBUSxJQUFDLEtBQUssRUFBRSxLQUFLLFlBQUcsUUFBUSxHQUFxQyxDQUFDO0FBQ3pHLENBQUMsQ0FBQztBQUVGOzs7R0FHRztBQUNILE1BQU0sQ0FBQyxNQUFNLG9CQUFvQixHQUFHLEdBQTJCLEVBQUUsQ0FBQyxVQUFVLENBQUMsd0JBQXdCLENBQUMsSUFBSSxFQUFFLENBQUMifQ==